import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography
} from '@mui/material';
import {
  ISSUE_CATEGORIES,
  ORDER_PRIORITY,
  ORDER_PRIORITY_OPTIONS,
  ORDER_TYPES
} from '../data/orderTypes';

const createInitialState = (preset = {}) => ({
  type: preset.type || ORDER_TYPES.SETUP,
  priority: preset.priority || ORDER_PRIORITY.NORMAL,
  dueDate: preset.dueDate || '',
  assignedTo: preset.assignedTo || '',
  description: preset.description || '',
  notes: preset.notes || '',
  residentId: preset.residentId || '',
  lockVendor: preset.lockVendor || '',
  lockName: preset.lockName || '',
  location: preset.location || '',
  lockId: preset.lockId || '',
  fromResidentId: preset.fromResidentId || '',
  toResidentId: preset.toResidentId || '',
  newLocation: preset.newLocation || '',
  issueCategory: preset.issueCategory || ISSUE_CATEGORIES[0],
  reportedBatteryLevel: preset.reportedBatteryLevel || ''
});

const OrderFormDialog = ({
  open,
  onClose,
  onSubmit,
  residents,
  locks,
  initialValues
}) => {
  const [form, setForm] = useState(createInitialState(initialValues));
  const [error, setError] = useState('');

  useEffect(() => {
    if (open) {
      setForm(createInitialState(initialValues));
      setError('');
    }
  }, [open, initialValues]);

  const selectedLock = useMemo(
    () => locks.find((lock) => lock.lockId === form.lockId),
    [locks, form.lockId]
  );

  const inferredFromResident = useMemo(() => {
    if (!selectedLock?.assignedResidents?.length) return '';
    return selectedLock.assignedResidents[0];
  }, [selectedLock]);

  useEffect(() => {
    if (form.type === ORDER_TYPES.MOVE) {
      setForm((prev) => ({
        ...prev,
        fromResidentId: prev.fromResidentId || inferredFromResident
      }));
    }
  }, [inferredFromResident, form.type]);

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const validate = () => {
    if (!form.description.trim()) {
      return 'Beskrivelse er påkrevd.';
    }

    if (form.type === ORDER_TYPES.SETUP && !form.residentId) {
      return 'Velg innbygger for oppsett.';
    }

    if (form.type === ORDER_TYPES.MOVE) {
      if (!form.lockId) return 'Velg lås som skal flyttes.';
      if (!form.toResidentId) return 'Velg mottaker av låsen.';
      if (form.toResidentId === form.fromResidentId) {
        return 'Fra- og til-innbygger kan ikke være samme person.';
      }
    }

    if (form.type === ORDER_TYPES.SERVICE && !form.lockId) {
      return 'Velg lås som trenger service.';
    }

    return '';
  };

  const handleSubmit = () => {
    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }

    setError('');
    onSubmit({
      ...form,
      fromResidentId: form.type === ORDER_TYPES.MOVE ? form.fromResidentId : '',
      completedAt: null
    });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Ny bestilling</DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 1 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Type bestilling
          </Typography>
          <ToggleButtonGroup
            exclusive
            value={form.type}
            onChange={(_, value) => value && handleChange('type', value)}
            sx={{ mb: 2 }}
          >
            <ToggleButton value={ORDER_TYPES.SETUP}>Oppsett</ToggleButton>
            <ToggleButton value={ORDER_TYPES.MOVE}>Flytting</ToggleButton>
            <ToggleButton value={ORDER_TYPES.SERVICE}>Service</ToggleButton>
          </ToggleButtonGroup>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                select
                label="Prioritet"
                value={form.priority}
                onChange={(e) => handleChange('priority', e.target.value)}
                SelectProps={{ native: true }}
              >
                {ORDER_PRIORITY_OPTIONS.map((priority) => (
                  <option key={priority} value={priority}>
                    {priority}
                  </option>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                type="date"
                label="Frist"
                value={form.dueDate}
                onChange={(e) => handleChange('dueDate', e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Tildelt til"
                value={form.assignedTo}
                onChange={(e) => handleChange('assignedTo', e.target.value)}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                required
                multiline
                minRows={2}
                label="Beskrivelse"
                value={form.description}
                onChange={(e) => handleChange('description', e.target.value)}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                minRows={2}
                label="Notater"
                value={form.notes}
                onChange={(e) => handleChange('notes', e.target.value)}
              />
            </Grid>
          </Grid>

          {form.type === ORDER_TYPES.SETUP && (
            <Grid container spacing={2} sx={{ mt: 0.5 }}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  select
                  required
                  label="Innbygger"
                  value={form.residentId}
                  onChange={(e) => handleChange('residentId', e.target.value)}
                  SelectProps={{ native: true }}
                >
                  <option value="">Velg innbygger...</option>
                  {residents.map((resident) => (
                    <option key={resident.residentId} value={resident.residentId}>
                      {resident.name} ({resident.room})
                    </option>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Lokasjon"
                  value={form.location}
                  onChange={(e) => handleChange('location', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Leverandør"
                  value={form.lockVendor}
                  onChange={(e) => handleChange('lockVendor', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Låsmodell/navn"
                  value={form.lockName}
                  onChange={(e) => handleChange('lockName', e.target.value)}
                />
              </Grid>
            </Grid>
          )}

          {form.type === ORDER_TYPES.MOVE && (
            <Grid container spacing={2} sx={{ mt: 0.5 }}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  select
                  required
                  label="Lås"
                  value={form.lockId}
                  onChange={(e) => handleChange('lockId', e.target.value)}
                  SelectProps={{ native: true }}
                >
                  <option value="">Velg lås...</option>
                  {locks.map((lock) => (
                    <option key={lock.lockId} value={lock.lockId}>
                      {lock.name} ({lock.location})
                    </option>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  select
                  required
                  label="Flyttes til innbygger"
                  value={form.toResidentId}
                  onChange={(e) => handleChange('toResidentId', e.target.value)}
                  SelectProps={{ native: true }}
                >
                  <option value="">Velg innbygger...</option>
                  {residents.map((resident) => (
                    <option key={resident.residentId} value={resident.residentId}>
                      {resident.name} ({resident.room})
                    </option>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Fra innbygger"
                  value={form.fromResidentId}
                  onChange={(e) => handleChange('fromResidentId', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Ny lokasjon"
                  value={form.newLocation}
                  onChange={(e) => handleChange('newLocation', e.target.value)}
                />
              </Grid>
            </Grid>
          )}

          {form.type === ORDER_TYPES.SERVICE && (
            <Grid container spacing={2} sx={{ mt: 0.5 }}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  select
                  required
                  label="Lås"
                  value={form.lockId}
                  onChange={(e) => handleChange('lockId', e.target.value)}
                  SelectProps={{ native: true }}
                >
                  <option value="">Velg lås...</option>
                  {locks.map((lock) => (
                    <option key={lock.lockId} value={lock.lockId}>
                      {lock.name} ({lock.location})
                    </option>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  select
                  label="Feilkategori"
                  value={form.issueCategory}
                  onChange={(e) => handleChange('issueCategory', e.target.value)}
                  SelectProps={{ native: true }}
                >
                  {ISSUE_CATEGORIES.map((category) => (
                    <option key={category} value={category}>
                      {category}
                    </option>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Rapportert batterinivå"
                  value={form.reportedBatteryLevel}
                  onChange={(e) => handleChange('reportedBatteryLevel', e.target.value)}
                />
              </Grid>
            </Grid>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Avbryt</Button>
        <Button variant="contained" onClick={handleSubmit}>
          Opprett bestilling
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default OrderFormDialog;
